import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

interface DeleteAccountDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  isDeleting: boolean;
}

export function DeleteAccountDialog({
  isOpen,
  onClose,
  onConfirm,
  isDeleting,
}: Readonly<DeleteAccountDialogProps>) {
  const { t } = useTranslation();
  const [confirmText, setConfirmText] = useState('');

  const isConfirmed = confirmText === 'DELETE';

  return (
    <Sheet
      open={isOpen}
      onOpenChange={() => {
        setConfirmText('');
        onClose();
      }}
    >
      <SheetContent side="right" className="w-full sm:max-w-md">
        <SheetHeader>
          <SheetTitle>{t('account.deleteTitle')}</SheetTitle>
          <SheetDescription>{t('account.deleteWarning')}</SheetDescription>
        </SheetHeader>
        <div className="space-y-4 p-4">
          <label className="text-sm font-medium">{t('account.deleteConfirmLabel')}</label>
          <Input
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder={t('account.deleteConfirmPlaceholder')}
            data-testid="delete-confirm-input"
          />
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose} disabled={isDeleting}>
              {t('quotes.cancel')}
            </Button>
            <Button
              variant="destructive"
              onClick={onConfirm}
              disabled={!isConfirmed || isDeleting}
              data-testid="delete-confirm-button"
            >
              {isDeleting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t('account.deleting')}
                </>
              ) : (
                t('account.deleteButton')
              )}
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
